import type { Chapter, Step } from "./types";

/**
 * Returns true if at least one step in the sequence defines a chapter heading.
 */
export function hasChapters(steps: Step[]): boolean {
  return steps.some((s) => typeof s.chapterTitle === "string" && s.chapterTitle.trim().length > 0);
}

/**
 * Resolves a linear sequence of steps into structured chapters.
 * When applied to a runtime tag-filtered timeline, it preserves sequential indexing and
 * automatically omits chapters whose steps were completely filtered out.
 */
export function resolveChapters(steps: Step[], defaultFirstTitle = "Introduction"): Chapter[] {
  if (steps.length === 0) return [];

  const chapters: Chapter[] = [];
  let current: Chapter | null = null;

  for (let idx = 0; idx < steps.length; idx++) {
    const s = steps[idx];
    const customTitle = s.chapterTitle?.trim();

    if (customTitle) {
      // Step begins an explicit new chapter
      current = {
        id: s.id,
        title: customTitle,
        firstStepId: s.id,
        firstStepIndex: idx,
        stepCount: 1,
        stepIds: [s.id],
      };
      chapters.push(current);
    } else if (!current) {
      // First steps before any explicit chapter heading
      current = {
        id: s.id,
        title: defaultFirstTitle,
        firstStepId: s.id,
        firstStepIndex: idx,
        stepCount: 1,
        stepIds: [s.id],
      };
      chapters.push(current);
    } else {
      // Step belongs to the active chapter
      current.stepCount++;
      current.stepIds.push(s.id);
    }
  }

  return chapters;
}

/**
 * Finds the chapter that currently contains the given step index.
 */
export function getActiveChapter(chapters: Chapter[], currentStepIndex: number): Chapter | null {
  for (let i = chapters.length - 1; i >= 0; i--) {
    if (currentStepIndex >= chapters[i].firstStepIndex) {
      return chapters[i];
    }
  }
  return chapters[0] ?? null;
}
